import { DotLegalLink, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Backdrop, Box, Paper, Popper } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import { RelativeDateConfigModel, RelativeDateDirection, RelativeDateUnit } from "../../../annualWheel/RelativeDate.types";
import { useRelativeDateConfigStyles } from "./RelativeDateConfig.styles";
import { useRelativeDateConfig } from "./RelativeDateConfig.hooks";

export interface RelativeDateConfigProps {
    label: string;
    config: RelativeDateConfigModel | undefined;
    calculatedDate: Date | null;
    useRelativeDate: boolean;
    linkLabel: string;
    closeLabel: string;
    contractStartDate?: Date;
    contractEndDate?: Date;
    error?: boolean;
    helperMessage?: string;
    onToggleRelativeDate: () => void;
    onValueChange: (value: string) => void;
    onUnitChange: (unit: string) => void;
    onDirectionChange: (direction: string) => void;
    onRelativeToChange: (relativeTo: string) => void;
    readOnly?: boolean;
    /**
     * For a caller whose label sits outside the field, so the field draws without margins. The link beneath
     * then keeps its distance itself, and sits as far below the field as it does under a field with margins.
     */
    noMargin?: boolean;
}

function RelativeDateConfig(props: RelativeDateConfigProps) {
    const { translateString, translateDate } = useTranslation();
    const styles = useRelativeDateConfigStyles();
    const { anchorElement, setAnchorElement, isEditing, hasContractDates, relativeToOptions, summary, openEditor, onSpecifyRelativeDateClick, closeEditor } =
        useRelativeDateConfig(props);

    if (!hasContractDates) {
        return null;
    }

    if (!props.useRelativeDate) {
        return (
            <Box sx={[styles.link, props.noMargin ? styles.linkBelowFieldWithoutMargin : styles.linkBelowFieldWithMargin]}>
                <DotLegalLink linkColor={"primary"} onClick={onSpecifyRelativeDateClick} size={"small"} disabled={props.readOnly}>
                    {props.linkLabel}
                </DotLegalLink>
            </Box>
        );
    }

    return (
        <>
            {/* The field only displays the relative date, so a click opens the controls instead of placing a caret. */}
            <Box ref={setAnchorElement} sx={props.readOnly ? undefined : styles.display} onClick={openEditor}>
                <DotLegalTextField
                    label={props.label}
                    value={summary}
                    startAdorment={props.calculatedDate ? translateDate(props.calculatedDate) : undefined}
                    debounce={false}
                    disabled={props.readOnly}
                    errorText={props.error ? props.helperMessage : undefined}
                    useFixedLabel={!!props.label}
                    noMargin={props.noMargin}
                />
            </Box>

            {/* Invisible, and underneath the popper: the selects inside it open their lists in a portal, which
                a click-away listener would take for a click outside and close the popper on. */}
            <Backdrop open={isEditing} invisible sx={styles.backdrop} onClick={closeEditor} />
            <Popper
                open={isEditing && !!anchorElement}
                anchorEl={anchorElement}
                placement="bottom-start"
                sx={styles.popper}
                modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
            >
                <Paper sx={styles.editor}>
                    <Box sx={styles.row}>
                        <Box sx={styles.value}>
                            <DotLegalTextField
                                onlyNumber={"integer"}
                                label={""}
                                value={props.config?.value?.toString() ?? ""}
                                debounce={false}
                                minNumberValue={0}
                                noMargin
                                autoFocus
                                onChange={props.onValueChange}
                            />
                        </Box>
                        <Box sx={styles.grow}>
                            <DotLegalSelect
                                selectedItem={props.config?.unit?.toString() ?? ""}
                                options={[
                                    { id: RelativeDateUnit.Days.toString(), name: translateString("days") },
                                    { id: RelativeDateUnit.Weeks.toString(), name: translateString("weeks") },
                                    { id: RelativeDateUnit.Months.toString(), name: translateString("months") },
                                    { id: RelativeDateUnit.Years.toString(), name: translateString("years") },
                                ]}
                                onChange={(unit) => props.onUnitChange(unit!)}
                                label={""}
                                placeholder={translateString("unit")}
                                noOptionsLabel={translateString("noOptions")}
                                disableClearable
                                noMargin
                            />
                        </Box>
                        <Box sx={styles.direction}>
                            <DotLegalSelect
                                selectedItem={props.config?.direction?.toString() ?? ""}
                                options={[
                                    { id: RelativeDateDirection.Before.toString(), name: translateString("before") },
                                    { id: RelativeDateDirection.After.toString(), name: translateString("after") },
                                ]}
                                onChange={(direction) => props.onDirectionChange(direction!)}
                                label={""}
                                placeholder={translateString("direction")}
                                noOptionsLabel={translateString("noOptions")}
                                disableClearable
                                noMargin
                            />
                        </Box>
                    </Box>
                    <Box sx={styles.row}>
                        <Box sx={styles.grow}>
                            <DotLegalSelect
                                selectedItem={props.config?.relativeTo?.toString() ?? ""}
                                options={relativeToOptions}
                                onChange={(relativeTo) => props.onRelativeToChange(relativeTo!)}
                                label={""}
                                placeholder={translateString("relativeTo")}
                                noOptionsLabel={translateString("noOptions")}
                                disableClearable
                                noMargin
                            />
                        </Box>
                    </Box>
                </Paper>
            </Popper>

            <Box sx={[styles.link, props.noMargin ? styles.linkBelowFieldWithoutMargin : styles.linkBelowFieldWithMargin]}>
                <DotLegalLink linkColor={"primary"} onClick={props.onToggleRelativeDate} size={"small"} disabled={props.readOnly}>
                    {props.closeLabel}
                </DotLegalLink>
            </Box>
        </>
    );
}

export default RelativeDateConfig;
